import { enablePointerEvents } from '@utils/pointer-events-control';
import { config, resetGameState } from '@js/state';
import { SPACE_BG, addStarfields } from '@utils/space';

/**
 * StartScene — the Figma "main game screen" (536:162) rebuilt layer-for-layer
 * at exact positions: starfields, sun (top-left), earth (bottom-right), the
 * dashed flight path, the title, a gently bobbing rocket, and the Play CTA.
 */
export default class StartScene extends Phaser.Scene {
  constructor() {
    super({ key: 'StartScene' });
  }

  create() {
    // Enable pointer events so hover states and the cursor work
    enablePointerEvents();

    this.cameras.main.setBackgroundColor(SPACE_BG);
    this.cameras.main.fadeIn(400, 0, 0, 0);

    addStarfields(this);
    this.add.image(-47, -63, 'sun').setDepth(0.5); // 536:436  601x601 @ c(-47,-63)
    this.add.image(1249, 989, 'earth').setDepth(0.5); // 536:433  1023x1023 @ c(1249,989)
    this.add.image(247, 432, 'flight-path').setDepth(1); // 536:445  @ c(247,432)

    this.add
      .text(644, 228, 'Artemis Math Mission', {
        fontFamily: 'Athletics-Regular',
        fontSize: '50px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(2);

    const rocket = this.add.image(646, 387, 'start-rocket').setDepth(2); // 536:439 @ c(646,387)
    this.tweens.add({
      targets: rocket,
      y: 375,
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.buildPlayCta(636, 624, () => this.startGame()); // 536:435 @ c(636,624)
    resetGameState();
  }

  buildPlayCta(x, y, onClick) {
    // ─── Pre-flight ───
    // Inputs: pointerup on the Play sprite.
    // Re-entry guards: disableInteractive on first pointerup, before onClick.
    // Manually verified: [x] double click [x] click during fade [x] rapid clicks
    const btn = this.add
      .image(x, y, 'play-button')
      .setOrigin(0.5)
      .setDepth(3)
      .setInteractive({ useHandCursor: true });
    // Hover = charcoal texture swap (Cuemath button-state convention), no scaling.
    btn.on('pointerover', () => btn.setTexture('play-button-hover'));
    btn.on('pointerout', () => btn.setTexture('play-button'));
    btn.on('pointerup', () => {
      btn.disableInteractive();
      onClick();
    });
  }

  startGame() {
    // BGM starts here — the Play click is the user gesture browsers require
    // before audio can play. The sound manager is game-global, so the loop
    // persists across scene changes; the guard stops a play-again double-start.
    if (!this.sound.get('bgm')) {
      this.sound.add('bgm', { loop: true, volume: 0.1 }).play();
    }
    this.cameras.main.fadeOut(400, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start('IntroScene'));
  }
}
